// How a content translation is presented in the UI, and the one place both apps
// derive that status from a stored `ContentTranslation` row. Pure logic (no
// React, no Prisma) so both apps can import it.

export type ContentTranslationUiStatus =
  | 'not-translated'
  | 'queued'
  | 'generating'
  // An AVAILABLE row where the worker left some segments in English (the
  // `?? segment` fallback, or the engine's tag-preservation guard keeping the
  // source when a translation mangles an inline tag).
  | 'available-partial'
  | 'available'
  | 'stale'
  | 'failed'
  // Deliberately stopped from the queue dashboard. Terminal like 'failed', but
  // rendered as its own quiet state so a stopped run is distinguishable from a
  // never-generated cell; re-requesting flips the same row back to PENDING.
  | 'cancelled';

// Narrowed to the fields the derivation reads, so callers pass the Prisma row
// without a cast.
export type TranslationStatusRow = {
  status: 'PENDING' | 'AVAILABLE' | 'FAILED' | 'CANCELLED';
  fileDrn: string | null;
  // The resourceVersionId this translation was produced from.
  sourceId: string;
  // Stamped when the worker picks the job up, so a queued PENDING row reads
  // differently from one actively generating.
  startedAt?: Date | string | null;
  // > 0 on a current AVAILABLE row means partial coverage. Null (a pre-signal
  // row) is treated as fully available.
  untranslatedSegmentCount?: number | null;
};

// `currentPublishedVersionId` is the resource's current published version, used
// to detect staleness; when unknown, staleness isn't asserted.
export function deriveTranslationStatus(
  row: TranslationStatusRow | undefined,
  currentPublishedVersionId: string | null | undefined,
): ContentTranslationUiStatus {
  if (row === undefined) {
    return 'not-translated';
  }
  if (row.status === 'FAILED') {
    return 'failed';
  }
  if (row.status === 'CANCELLED') {
    return 'cancelled';
  }
  if (row.status === 'PENDING') {
    return row.startedAt == null ? 'queued' : 'generating';
  }
  // AVAILABLE without an artifact isn't ready yet.
  if (row.fileDrn === null) {
    return 'queued';
  }
  if (currentPublishedVersionId != null && row.sourceId !== currentPublishedVersionId) {
    return 'stale';
  }
  if (row.untranslatedSegmentCount != null && row.untranslatedSegmentCount > 0) {
    return 'available-partial';
  }
  return 'available';
}
